import { Link } from "react-router-dom";
import { ArrowRight, Microscope, ScanEye, Armchair } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/motion";
import { PhotoSlot } from "../components/PhotoSlot";
import { GalleryGrid } from "../components/GalleryGrid";
import { EQUIPMENT_ITEMS } from "../lib/constants";

const Technology = () => (
  <>
    <PageHero
      kicker="Facilities & Technology"
      testid="technology-hero"
      lines={[
        <>Advanced technology,</>,
        <>
          <span className="font-serif italic font-semibold text-brand-bright">modern</span> eye care.
        </>,
      ]}
      sub="Modern eye care requires accurate diagnosis. iNetra combines specialist clinical expertise with modern ophthalmic diagnostic and examination technology."
    />

    <section className="py-20 sm:py-28" data-testid="technology-equipment-section">
      <div className="container-x">
        <div className="grid gap-5 sm:grid-cols-3 mb-16">
          {[
            { icon: ScanEye, title: "Diagnostic Imaging", desc: "Modern ophthalmic diagnostic equipment for accurate retinal and anterior segment evaluation." },
            { icon: Microscope, title: "Examination Suite", desc: "A well-equipped examination area with refraction unit and examination chair for comfortable, precise assessments." },
            { icon: Armchair, title: "Patient Comfort", desc: "A calm, clean clinical environment designed to make every visit relaxed and unhurried." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-7" data-testid={`facility-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-mist flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-brand-bright" strokeWidth={1.6} aria-hidden="true" />
                </div>
                <h3 className="mt-5 text-lg font-extrabold text-ink">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <SectionHead
          chapter="01"
          kicker="Diagnostic Equipment"
          title="Diagnostic equipment at iNetra"
          sub="Photographed at our Janak Puri centre — modern ophthalmic diagnostic and examination technology for accurate, comfortable evaluation."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {EQUIPMENT_ITEMS.map((e, i) => (
            <Reveal key={e.label} delay={(i % 4) * 0.07}>
              <PhotoSlot
                label={e.label}
                note=""
                src={e.src}
                alt={e.alt}
                className="aspect-[4/3] rounded-3xl card-lift"
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 bg-slate-50" data-testid="technology-gallery-section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            chapter="02"
            kicker="Clinic Gallery"
            title="Inside iNetra"
            sub="Reception, examination areas and clinical environment — photographed at our Janak Puri centre."
          />
          <Reveal delay={0.1}>
            <Link to="/contact" className="btn-outline" data-testid="technology-visit-button">
              Plan Your Visit <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12">
          <GalleryGrid />
        </div>
      </div>
    </section>
  </>
);

export default Technology;
